import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Layout from './components/Layout';
import ProjectsPage from './pages/ProjectsPage';
import TimeEntry from './pages/TimeEntry';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Today from './pages/Today';
import Timesheet from './pages/Timesheet';
import MyTasks from './pages/MyTasks';
import Login from './pages/Login';
import { PeopleProvider } from './context/PeopleContext';
import { AppRefreshProvider } from './context/AppRefreshContext';
import { Toaster } from 'react-hot-toast';
import ManagerDashboard from './pages/ManagerDashboard';
import ControlPanel from './pages/ControlPanel';
import Projects from './pages/Projects';
import ProjectPage from './pages/ProjectPage';
import CalendarStub from './pages/CalendarStub';
import FeedbackPage from './pages/FeedbackPage';
import Vacation from './pages/Vacation';
import PeopleV2Page from './pages/People/PeopleV2Page.jsx';
import EmployeeIntelligencePage from './pages/People/EmployeeIntelligencePage.jsx';
import { API_BASE_URL, updateMyUiTheme } from './api';
import { isLocalOnlyThemeName, UI_THEME_KEYS } from './ui/tokens';
import toast from 'react-hot-toast';

const ALLOWED_THEME_KEYS = new Set(UI_THEME_KEYS);

function normalizeThemeKey(raw, fallback = 'auroraExecutiveLight') {
  const v = String(raw || '').trim();
  if (!v) return fallback;
  // Migrations: removed themes -> Aurora production themes
  if (v === 'dark') return 'auroraExecutive';
  if (v === 'light') return 'auroraExecutiveLight';
  if (v === 'neonNight') return 'auroraExecutive';
  if (v === 'neonDay') return 'auroraExecutiveLight';
  if (v === 'neonDayWarmFocus') return 'auroraExecutiveLight';
  if (v === 'neonDayBlushMinimal') return 'auroraExecutiveLight';
  if (v === 'neonDayPressureMode') return 'auroraExecutiveLight';
  if (v === 'neon_night') return 'auroraExecutive';
  if (v === 'neon_day') return 'auroraExecutiveLight';
  return ALLOWED_THEME_KEYS.has(v) ? v : fallback;
}

function TasksRouteShim() {
  // Some parts of the app link to /tasks?... but this codebase uses /projects + /projects/:id.
  // Redirect to /projects while preserving query params (assignee/status/period/taskId/etc).
  const location = useLocation();
  return <Navigate to={`/projects${location.search || ''}`} replace />;
}

function PeopleRouteShim() {
  // Preserve query params (filter/period/sort/etc) when redirecting to the real people page.
  const location = useLocation();
  return <Navigate to={`/people-logs${location.search || ''}`} replace />;
}

function AppShell() {
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => {
    try {
      const raw = localStorage.getItem('theme_preference') || 'auroraExecutiveLight';
      const normalized = normalizeThemeKey(raw, 'auroraExecutiveLight');
      if (normalized !== raw) localStorage.setItem('theme_preference', normalized);
      return normalized;
    } catch {
      return 'auroraExecutiveLight';
    }
  });
  const isDark = theme === 'auroraExecutive';
  const [user, setUser] = useState(null); // { email, display_name, role }
  const redirectedRef = useRef(false);
  const [loadingUser, setLoadingUser] = useState(false);

  const defaultPathForRole = (r) => (r === 'manager' || r === 'admin' || r === 'junior_manager' ? '/dashboard' : '/my-work');

  const canUseRed2 = (u) => String(u?.email || '').trim().toLowerCase() === 'admin@example.com';

  const enforceThemeVisibilityForUser = async (u, candidateTheme) => {
    const next = String(candidateTheme || '').trim();
    if (!next) return null;
    if (next !== 'red2') return next;
    if (canUseRed2(u)) return next;
    const downgraded = 'redLightPremium';
    setTheme(downgraded);
    try { localStorage.setItem('theme_preference', downgraded); } catch {}
    try {
      if (!isLocalOnlyThemeName(downgraded)) await updateMyUiTheme(downgraded);
    } catch {}
    return downgraded;
  };

  const applyThemeFromUser = (u) => {
    const next = normalizeThemeKey(u?.ui_theme, null);
    if (!next) return;

    // Local-only preview themes should not be overwritten by server settings.
    try {
      const ls = localStorage.getItem('theme_preference');
      if (isLocalOnlyThemeName(ls)) return;
    } catch {}

    // Red 2 is admin-only (admin@example.com)
    if (next === 'red2' && !canUseRed2(u)) {
      void enforceThemeVisibilityForUser(u, next);
      return;
    }

    setTheme(next);
    try { localStorage.setItem('theme_preference', next); } catch {}
  };

  // If a non-admin somehow has red2 stored locally, downgrade after user loads.
  useEffect(() => {
    if (!user) return;
    if (theme !== 'red2') return;
    if (canUseRed2(user)) return;
    void enforceThemeVisibilityForUser(user, theme);
  }, [user, theme]);

  const handleLogin = (u) => {
    setUser(u || null);
    applyThemeFromUser(u);
  };

  const handleLogout = () => {
    try { window.dispatchEvent(new Event('auth:logout')); } catch {}
    try { localStorage.removeItem('token'); } catch {}
    setUser(null);
    redirectedRef.current = false;
    navigate('/login', { replace: true });
  };

  // Attempt silent auth on page load if token exists
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoadingUser(true);
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const json = await res.json();
          if (!cancelled) {
            const u = json.user || null;
            setUser(u);
            applyThemeFromUser(u);
          }
        } else {
          localStorage.removeItem('token');
        }
      } catch (_) {}
      finally {
        if (!cancelled) setLoadingUser(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Persist theme to localStorage whenever it changes
  useEffect(() => {
    try { localStorage.setItem('theme_preference', theme); } catch {}
  }, [theme]);

  // Redirect after login based on role
  useEffect(() => {
    if (!user || redirectedRef.current) return;
    redirectedRef.current = true;
    navigate(defaultPathForRole(user.role), { replace: true });
  }, [user, navigate]);

  function ProtectedRoute({ allowedRoles, children }) {
    const token = (() => { try { return localStorage.getItem('token'); } catch { return null; } })();
    if (!token) return <Navigate to="/login" replace />;
    if (!user) {
      return loadingUser ? <div className="p-6 text-sm text-gray-600">Loading…</div> : <Navigate to="/login" replace />;
    }
    if (!allowedRoles.includes(user.role)) {
      return <Navigate to={defaultPathForRole(user.role)} replace />;
    }
    return children;
  }

  return (
    <>
      <Toaster position="top-right" />
      <AppRefreshProvider>
        <PeopleProvider>
          <Routes>
          {/* Login route */}
          <Route path="/login" element={user ? <Navigate to={defaultPathForRole(user.role)} replace /> : <Login onLogin={handleLogin} />} />

          {/* Protected layout wrapper for all app pages */}
          <Route element={
            <ProtectedRoute allowedRoles={['manager','admin','employee','junior_manager']}>
              <Layout
                user={user}
                darkMode={isDark}
                setDarkMode={(v)=> setTheme(v ? 'auroraExecutive' : 'auroraExecutiveLight')}
                onLogout={handleLogout}
                theme={theme}
                onThemeChange={async (t) => {
                  const prev = theme;
                  const normalized = normalizeThemeKey(t, prev || 'auroraExecutiveLight');
                  const next = (normalized === 'red2' && !canUseRed2(user)) ? 'redLightPremium' : normalized;
                  setTheme(next);
                  try { localStorage.setItem('theme_preference', next); } catch {}
                  if (isLocalOnlyThemeName(next)) return;
                  try {
                    await updateMyUiTheme(next);
                  } catch (err) {
                    const msg = String(err?.message || '').trim();
                    if (msg === 'Invalid ui_theme') {
                      toast.error('Theme applied locally (server rejected key)');
                      return;
                    }
                    setTheme(prev);
                    try { localStorage.setItem('theme_preference', prev); } catch {}
                    toast.error('Failed to save theme');
                  }
                }}
              />
            </ProtectedRoute>
          }>
            {/* Default landing per role */}
            <Route path="/" element={user ? <Navigate to={defaultPathForRole(user.role)} replace /> : <Navigate to="/login" replace />} />

            {/* Employee-only */}
            <Route path="/my-work" element={
              <ProtectedRoute allowedRoles={["employee", "junior_manager"]}>
                <MyTasks />
              </ProtectedRoute>
            } />

            {/* Manager-only */}
            <Route path="/dashboard" element={
              <ProtectedRoute allowedRoles={["manager","admin","junior_manager"]}>
                <ManagerDashboard />
              </ProtectedRoute>
            } />

            <Route path="/control-panel" element={
              <ProtectedRoute allowedRoles={["manager","admin","junior_manager"]}>
                <ControlPanel />
              </ProtectedRoute>
            } />
            <Route path="/projects" element={
              <ProtectedRoute allowedRoles={["manager","admin","junior_manager"]}>
                <Projects />
              </ProtectedRoute>
            } />
            <Route path="/tasks" element={
              <ProtectedRoute allowedRoles={["manager","admin","junior_manager"]}>
                <TasksRouteShim />
              </ProtectedRoute>
            } />
            <Route path="/projects/:id" element={
              <ProtectedRoute allowedRoles={["manager","admin","junior_manager"]}>
                <ProjectPage />
              </ProtectedRoute>
            } />
            <Route path="/calendar" element={
              <ProtectedRoute allowedRoles={["manager","admin","junior_manager"]}>
                <CalendarStub />
              </ProtectedRoute>
            } />

            <Route path="/people-logs" element={
              <ProtectedRoute allowedRoles={["manager","admin","junior_manager"]}>
                <PeopleV2Page />
              </ProtectedRoute>
            } />

            <Route path="/people/:personId" element={
              <ProtectedRoute allowedRoles={["manager","admin","junior_manager"]}>
                <EmployeeIntelligencePage />
              </ProtectedRoute>
            } />

            <Route path="/feedback" element={
              <ProtectedRoute allowedRoles={["manager","admin"]}>
                <FeedbackPage />
              </ProtectedRoute>
            } />

            {/* Shared */}
            <Route path="/reports" element={
              <ProtectedRoute allowedRoles={["manager","admin","junior_manager"]}>
                <Reports />
              </ProtectedRoute>
            } />

            <Route path="/vacation" element={
              <ProtectedRoute allowedRoles={["manager","admin"]}>
                <Vacation />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute allowedRoles={["manager","admin","employee","junior_manager"]}>
                <Settings user={user} theme={theme} onThemeChange={async (t) => {
                  const prev = theme;
                  const normalized = normalizeThemeKey(t, prev || 'auroraExecutiveLight');
                  const next = (normalized === 'red2' && !canUseRed2(user)) ? 'redLightPremium' : normalized;
                  setTheme(next);
                  try { localStorage.setItem('theme_preference', next); } catch {}
                  if (isLocalOnlyThemeName(next)) return;
                  try {
                    await updateMyUiTheme(next);
                  } catch (err) {
                    const msg = String(err?.message || '').trim();
                    if (msg === 'Invalid ui_theme') {
                      toast.error('Theme applied locally (server rejected key)');
                      return;
                    }
                    setTheme(prev);
                    try { localStorage.setItem('theme_preference', prev); } catch {}
                    toast.error('Failed to save theme');
                  }
                }} />
              </ProtectedRoute>
            } />

            {/* Legacy redirects */}
            <Route path="/today" element={<Navigate to="/my-work" replace />} />
            <Route path="/timesheet" element={<Navigate to="/my-work" replace />} />
            <Route path="/my-tasks" element={<Navigate to="/my-work" replace />} />
            <Route path="/people" element={<PeopleRouteShim />} />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to={user ? defaultPathForRole(user.role) : '/login'} replace />} />
          </Route>
        </Routes>
        </PeopleProvider>
      </AppRefreshProvider>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

export default App;
